/* eslint-disable camelcase */
/* eslint-disable react/jsx-no-bind */
import { useCallback, useContext, useEffect, useState } from 'react';

import { Box, Drawer } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { differenceInCalendarDays } from 'date-fns';
import withAuth from 'hocs/withAuth/withAuth';
import uniqBy from 'lodash.uniqby';
import InfiniteScroll from 'react-infinite-scroll-component';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { objectSortFn } from 'utils/helpers/sorting';

import { useModals } from 'context';
import {
  getSpecialistRole,
  SpecialistContext,
} from 'context/SpecialistContext/SpecialistContext';
import { useComplaintDevice, useRandomColors, useRequest } from 'hooks';
import { useCareMatrix } from 'hooks/useCareMatrix';
import { SCREEN_MIN_WIDTH } from 'hooks/useComplaintDevice/useComplaintDevice';
import {
  getSpecialistChatUsers,
  SpecialistChatUser,
  SpecialistRole,
} from 'services/specialist';

import {
  ChatList,
  ChatListItem,
  ChatConversation,
  ChatListEmptyState,
} from 'components';
import { CareMatrixModule } from 'components/CareMatrixModule';
import { ActiveChatUser } from 'components/ChatConversation/types';
import {
  listFilterToListTab,
  ListTab,
  listTabToListFilter,
  SelectableListFilters,
} from 'components/ChatList/types';

import { ListFilter } from './types';

import * as S from './styles';

const Chat: React.FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const setPatientsRequest = useRequest();
  const { openModal, updateModal, closeModal } = useModals();
  const [searchParams, setSearchParams] = useSearchParams();
  const cognitoIdSearchParam = searchParams.get('cognito_id') || undefined;
  const { handleSetComplaintDevice, isComplaintDevice } = useComplaintDevice();

  const { isModuleOpen, closeModule, openModule } = useCareMatrix();
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  const [cognitoID, setCognitoID] = useState('');

  const { specialistInfo } = useContext(SpecialistContext);
  const specialistRole = getSpecialistRole(specialistInfo?.groups);

  const [euNameSearch, setEuNameSearch] = useState('');
  const [activeChatuser, setActiveChatUser] = useState<ActiveChatUser>();
  const [patientsModalOpen, setPatientsModalOpen] = useState<boolean>(false);

  const [listFilter, setListFilter] = useState<ListFilter>();

  const [allChatUsers, setAllChatUsers] = useState<SpecialistChatUser[]>([]);
  const [chatLength, setChatLength] = useState<number>(0);
  const [hasMoreChats, setHasMoreChats] = useState<boolean>(false);
  const queryClient = useQueryClient();

  const [euActiveChatUsers, setEuActiveChatUsers] = useState<
    SpecialistChatUser[]
  >([]);
  const [someActiveChatUsers, setSomeActiveChatUsers] = useState<
    SpecialistChatUser[]
  >([]);
  const [euNotStartedChatUsers, setEuNotStartedChatUsers] = useState<
    SpecialistChatUser[]
  >([]);
  const [euNameSearchChatUsers, setEuNameSearchChatUsers] = useState<
    SpecialistChatUser[]
  >([]);
  const [unreadChatUsers, setUnreadChatUsers] = useState<SpecialistChatUser[]>(
    [],
  );
  const [scheduledMessagesChatUsers, setScheduledMessagesChatUsers] = useState<
    SpecialistChatUser[]
  >([]);

  const { colors: allColors } = useRandomColors(allChatUsers.length);

  const { colors: someActiveColors } = useRandomColors(
    someActiveChatUsers.length,
  );
  const { colors: euActiveColors } = useRandomColors(euActiveChatUsers.length);

  const { colors: euNotStartedColor } = useRandomColors(
    euNotStartedChatUsers.length,
  );

  const { colors: euNameSearchColors } = useRandomColors(
    euNameSearchChatUsers.length,
  );

  const { colors: unreadColors } = useRandomColors(unreadChatUsers.length);

  const { colors: scheduledMessagesColors } = useRandomColors(
    scheduledMessagesChatUsers.length,
  );

  const activeChatNameSearchUsers = euNameSearchChatUsers.filter(
    chatUser => chatUser.chat.has_chat_message,
  );
  const inactiveChatNameSearchUsers = euNameSearchChatUsers.filter(
    chatUser => !chatUser.chat.has_chat_message,
  );

  const isMobile = window.innerWidth <= SCREEN_MIN_WIDTH;

  const selectedFilter: SelectableListFilters =
    (
      [
        ListFilter.UNREAD_ONLY,
        ListFilter.SCHEDULED_MESSAGES_ONLY,
      ] as SelectableListFilters[]
    ).find(selectableListFilter => selectableListFilter === listFilter) || null;

  const handleSelectConversation = (cognitoId: string) => {
    setCognitoID(cognitoId);
  };

  const handleCloseConversation = () => {
    setActiveChatUser(undefined);
    setCognitoID('');
  };

  const defaultFilterBySpecialist = () => {
    if (specialistRole === SpecialistRole.TC) {
      return ListFilter.EU_ACTIVE;
    }
    if (specialistRole === SpecialistRole.CP) {
      return ListFilter.SOME_ACTIVE;
    }
    return undefined;
  };

  const defaultFilter = defaultFilterBySpecialist();

  const getCurrentPatientsList = () => {
    const listFilterToPatientsListMap = {
      [ListFilter.ALL]: allChatUsers,
      [ListFilter.EU_ACTIVE]: euActiveChatUsers,
      [ListFilter.SOME_ACTIVE]: someActiveChatUsers,
      [ListFilter.EU_NOT_STARTED]: euNotStartedChatUsers,
      [ListFilter.EU_NAME_SEARCH]: euNameSearchChatUsers,
      [ListFilter.UNREAD_ONLY]: unreadChatUsers,
      [ListFilter.SCHEDULED_MESSAGES_ONLY]: scheduledMessagesChatUsers,
    };

    if (listFilter) {
      return listFilterToPatientsListMap[listFilter];
    }

    return undefined;
  };

  const currentPatientsList = getCurrentPatientsList();

  const getAvatarColor = (userIndex: number) => {
    const filterToColorArrayMap = {
      [ListFilter.ALL]: allColors,
      [ListFilter.EU_ACTIVE]: euActiveColors,
      [ListFilter.SOME_ACTIVE]: someActiveColors,
      [ListFilter.EU_NOT_STARTED]: euNotStartedColor,
      [ListFilter.EU_NAME_SEARCH]: euNameSearchColors,
      [ListFilter.UNREAD_ONLY]: unreadColors,
      [ListFilter.SCHEDULED_MESSAGES_ONLY]: scheduledMessagesColors,
    };

    if (listFilter) {
      return filterToColorArrayMap[listFilter][userIndex];
    }

    return undefined;
  };

  const title = intl.formatMessage({ id: 'CHAT.PAGE.TITLE' });

  const handleFilterPatients = (filterValue: string) => {
    setEuNameSearch(filterValue);

    if (filterValue) {
      setListFilter(ListFilter.EU_NAME_SEARCH);
    } else {
      setListFilter(defaultFilter);
    }
  };

  const handleSelectFilter = (filter: SelectableListFilters) => {
    if (filter === null) {
      setListFilter(defaultFilter);
      return;
    }
    setEuNameSearch('');
    setListFilter(filter);
  };

  const handlePatientsChatList = async (ignoreCursor = false) => {
    setPatientsRequest.setLoading();

    const fetchChatUsers = async ({
      queryParams,
      currentChatUsers,
      setChatUsers,
      sortBy,
    }: {
      queryParams: Record<string, unknown>;
      currentChatUsers: SpecialistChatUser[];
      setChatUsers: (chatUsers: SpecialistChatUser[]) => void;
      sortBy?: 'lastMessages' | 'userName';
    }) => {
      const { data, metadata } = await queryClient.fetchQuery({
        queryKey: ['getSpecialistChatUsers'],
        queryFn: () =>
          getSpecialistChatUsers({
            ...queryParams,
            nextCursor: ignoreCursor ? undefined : nextCursor,
          }),
      });

      if (metadata.nextCursor) {
        setHasMoreChats(true);
        setNextCursor(metadata.nextCursor);
      } else {
        setHasMoreChats(false);
        setNextCursor(undefined);
      }

      if (data.length) {
        const allChats = [...currentChatUsers, ...data];
        const uniqChats = uniqBy(allChats, patient => patient.user.id);

        setChatLength(uniqChats.length);

        if (sortBy === 'userName') {
          setChatUsers(objectSortFn(uniqChats, 'user.full_name', 'ASC'));
        } else {
          setChatUsers(
            objectSortFn(
              uniqChats,
              'chat.last_message_delivered_at',
              'DESC',
              'date',
            ),
          );
        }
      }
    };

    try {
      if (listFilter === ListFilter.ALL) {
        await fetchChatUsers({
          queryParams: {},
          currentChatUsers: allChatUsers,
          setChatUsers: setAllChatUsers,
        });
      }

      if (listFilter === ListFilter.EU_ACTIVE) {
        await fetchChatUsers({
          queryParams: { hasPatientInteraction: true },
          currentChatUsers: euActiveChatUsers,
          setChatUsers: setEuActiveChatUsers,
        });
      }

      if (listFilter === ListFilter.EU_NAME_SEARCH) {
        await fetchChatUsers({
          queryParams: { userName: euNameSearch },
          currentChatUsers: [],
          setChatUsers: setEuNameSearchChatUsers,
          sortBy: 'userName',
        });
      }

      if (listFilter === ListFilter.EU_NOT_STARTED) {
        await fetchChatUsers({
          queryParams: { hasPatientInteraction: false },
          currentChatUsers: euNotStartedChatUsers,
          setChatUsers: setEuNotStartedChatUsers,
        });
      }

      if (listFilter === ListFilter.SOME_ACTIVE) {
        await fetchChatUsers({
          queryParams: { hasChatMessage: true },
          currentChatUsers: someActiveChatUsers,
          setChatUsers: setSomeActiveChatUsers,
        });
      }

      if (listFilter === ListFilter.UNREAD_ONLY) {
        await fetchChatUsers({
          queryParams: { hasUnreadMessage: true },
          currentChatUsers: unreadChatUsers,
          setChatUsers: setUnreadChatUsers,
        });
      }

      if (listFilter === ListFilter.SCHEDULED_MESSAGES_ONLY) {
        await fetchChatUsers({
          queryParams: { hasScheduledMessage: true },
          currentChatUsers: scheduledMessagesChatUsers,
          setChatUsers: setScheduledMessagesChatUsers,
        });
      }

      if (cognitoIdSearchParam) {
        const { data } = await queryClient.fetchQuery({
          queryKey: ['getSpecialistChatUsers'],
          queryFn: () =>
            getSpecialistChatUsers({
              cognitoId: cognitoIdSearchParam,
            }),
        });

        if (data.length === 1) {
          handleFilterPatients(data[0].user.full_name);
          setSearchParams(urlParams => {
            urlParams.delete('cognito_id');
            return urlParams;
          });
          handleSelectConversation(cognitoIdSearchParam);
          openModule(cognitoIdSearchParam);
        }
      }

      setPatientsRequest.setSuccess();
    } catch (e) {
      setPatientsRequest.setError(e);
    }
  };

  const handleOpenModal = () => {
    setPatientsModalOpen(true);
    setActiveChatUser(undefined);
    openModal('chatListModal', {
      props: {
        chatList: currentPatientsList,
        onFilterPatients: handleFilterPatients,
        next: handlePatientsChatList,
        hasMore: hasMoreChats,
        onSelectConversation: handleSelectConversation,
      },
      onClose: () => {
        setListFilter(defaultFilter);
        setPatientsModalOpen(false);
      },
    });
  };

  const handleNewMessage = () => {
    setListFilter(ListFilter.ALL);
    handleOpenModal();
  };

  const handleTabClick = (listTab: ListTab) => {
    setListFilter(listTabToListFilter(listTab));
  };

  const renderListItems = useCallback(
    ({ user, chat }: SpecialistChatUser, avatarColor?: string) => (
      <ChatListItem
        key={user.id}
        avatarColor={avatarColor}
        fullName={user.full_name}
        endUserCognitoId={user.cognito_id}
        unreadMessages={chat.total_unreaded_message}
        hasUnreadedMessage={chat.has_unreaded_message}
        lastMessageDeliveredAt={chat.last_message_delivered_at}
        isSelected={chat.conversation_id === activeChatuser?.conversationId}
        daysUntilExpire={
          user.expired_at
            ? differenceInCalendarDays(new Date(user.expired_at), new Date())
            : undefined
        }
        onSelect={handleSelectConversation}
        hasScheduledMessage={chat.has_scheduled_messages}
        isCritical={user.is_critical}
      />
    ),
    [activeChatuser],
  );

  const getListItemsByCurrentFilter = () => {
    if (
      listFilter === ListFilter.EU_NAME_SEARCH &&
      specialistRole !== SpecialistRole.TC
    ) {
      return (
        <>
          {euNameSearchChatUsers.length ? (
            activeChatNameSearchUsers.map((userChat, index) =>
              renderListItems(userChat, getAvatarColor(index)),
            )
          ) : (
            <ChatListEmptyState />
          )}
          {!!inactiveChatNameSearchUsers.length && (
            <>
              <S.AllPatientsTitle data-testid="all-patients-tittle">
                <FormattedMessage id="CHAT.LIST.INACTIVE_CHAT_USERS.TITLE" />
              </S.AllPatientsTitle>
              {inactiveChatNameSearchUsers.map((userChat, index) =>
                renderListItems(userChat, getAvatarColor(index)),
              )}
            </>
          )}
        </>
      );
    }

    if (listFilter === ListFilter.ALL) {
      return someActiveChatUsers.length ? (
        someActiveChatUsers.map((userChat, index) =>
          renderListItems(userChat, someActiveColors[index]),
        )
      ) : (
        <ChatListEmptyState />
      );
    }

    return currentPatientsList?.length ? (
      currentPatientsList?.map((userChat, index) =>
        renderListItems(userChat, getAvatarColor(index)),
      )
    ) : (
      <ChatListEmptyState />
    );
  };

  const handleGetCurrentPatient = async () => {
    setPatientsRequest.setLoading();
    try {
      const patientsArray = [...(currentPatientsList || []), ...allChatUsers];
      const userByParam = patientsArray.find(
        patient => patient.user.cognito_id === cognitoID,
      );

      if (!userByParam && listFilter && !activeChatuser) {
        // Change automatically to the next filter in the array, case the current user does not exist in the current one
        const getFiltersArray = () => {
          if (specialistRole === SpecialistRole.TC) {
            return [ListFilter.EU_ACTIVE, ListFilter.EU_NOT_STARTED];
          }
          return [ListFilter.SOME_ACTIVE, ListFilter.ALL];
        };

        const filtersArray = getFiltersArray();

        const currentIndex = filtersArray.indexOf(listFilter);
        const nextFilter = filtersArray[currentIndex + 1];

        if (nextFilter) {
          setListFilter(nextFilter);
        } else {
          navigate('/care-provider/chat', { replace: true });
        }
      }

      if (userByParam) {
        const {
          user: { full_name, id, expired_at },
          chat: { conversation_id, scheduled_messages },
          keys,
        } = userByParam;

        const activeUserByParam = {
          patientId: id,
          conversationId: conversation_id,
          fullName: full_name,
          endUserCognitoId: cognitoID,
          expiredAt: expired_at,
          scheduledMessage: scheduled_messages[0],
          keys,
        };

        setActiveChatUser(activeUserByParam);
        setPatientsRequest.setSuccess();
      }
    } catch (e) {
      setPatientsRequest.setError(e);
    }
  };

  useEffect(() => {
    setListFilter(defaultFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specialistRole]);

  useEffect(() => {
    handlePatientsChatList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [euNameSearch, patientsModalOpen]);

  useEffect(() => {
    handlePatientsChatList(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listFilter]);

  useEffect(() => {
    if (cognitoID && listFilter && currentPatientsList?.length) {
      handleGetCurrentPatient();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cognitoID, listFilter, currentPatientsList]);

  useEffect(() => {
    if (patientsModalOpen) {
      updateModal('chatListModal', {
        props: {
          chatList: currentPatientsList,
          next: handlePatientsChatList,
          hasMore: hasMoreChats,
        },
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientsModalOpen, currentPatientsList]);

  useEffect(() => {
    if (activeChatuser) {
      const selectedElement = document.getElementById(
        `chat-list-item-${activeChatuser.endUserCognitoId}`,
      );
      if (selectedElement) {
        selectedElement.scrollIntoView();
      }
    }
  }, [activeChatuser]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => closeModal('chatListModal'), []);

  useEffect(() => {
    return closeModule();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (cognitoID) {
      openModule(cognitoID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    handleSetComplaintDevice(true);
  }, [handleSetComplaintDevice, isComplaintDevice]);

  return (
    <S.Container bgColor="white" d="flex" data-testid="contentChat">
      <S.ChatListContainer>
        <ChatList
          title={title}
          onTabClick={handleTabClick}
          onClickNewMessage={handleNewMessage}
          onFilterPatients={handleFilterPatients}
          filterValue={euNameSearch}
          listTab={listFilterToListTab(listFilter)}
          selectedFilter={selectedFilter}
          onSelectFilter={handleSelectFilter}
        >
          <InfiniteScroll
            next={handlePatientsChatList}
            hasMore={hasMoreChats}
            loader={undefined}
            dataLength={chatLength}
            scrollableTarget="scrollableDiv"
          >
            <div data-testid="chatUserslistItems">
              {getListItemsByCurrentFilter()}
            </div>
          </InfiniteScroll>
        </ChatList>
      </S.ChatListContainer>
      <ChatConversation
        activeChatUser={activeChatuser}
        onClickNewMessage={handleNewMessage}
        patientsListMode={patientsModalOpen}
        noActiveUsers={!someActiveChatUsers.length}
      />

      {isModuleOpen && <CareMatrixModule />}
      {!!cognitoID && isMobile && (
        <Drawer
          open={!!cognitoID && isMobile}
          sx={{ '.MuiDrawer-paper': { backgroundColor: 'white' } }}
          PaperProps={{ sx: { width: '100%' } }}
          anchor="right"
        >
          <Box width="100%" height="100%">
            <ChatConversation
              activeChatUser={activeChatuser}
              onClickNewMessage={handleNewMessage}
              patientsListMode={patientsModalOpen}
              noActiveUsers={!someActiveChatUsers.length}
              closeConversation={handleCloseConversation}
            />
          </Box>
        </Drawer>
      )}
    </S.Container>
  );
};

export default withAuth(Chat);
